import { useMemo, useState } from "react";
import { ChevronDown, Download } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { FileDropZone } from "@/components/ui/FileDropZone";
import { Modal } from "@/components/ui/Modal";
import { Spinner } from "@/components/ui/Spinner";
import { useAuthStore } from "@/store/auth";
import {
  useFormatkaDefaults,
  useFormatkaTemplateMutations,
  useFormatkaTemplateVersions,
  useFormatkaTemplates,
  useFormatkaVariables,
  useSaveFormatkaDefaults,
} from "../../hooks";
import { SHARED_OPERATOR, operatorShort } from "../../operatorTemplates";
import type { FormatkaPaper, FormatkaScope, FormatkaTemplate } from "../../types";
import { FORMATKA_PAPER_LABEL, FORMATKA_SCOPE_LABEL } from "../../types";
import { fmtDateTime } from "../../utils";
import { ManagedFileDownload, TableShell, Th } from "../ui";

/**
 * Sekcja „Szablony formatek (DXF)" w Generacji dokumentów — rejestr szablonów
 * arkuszy „plan sytuacyjny": lista, upload, edycja + wgrywanie nowych wersji
 * pliku (historia wgrań jak przy szablonach ODF), siatka domyślnych szablonów
 * i instrukcja przygotowania DXF.
 *
 * Kto edytuje/usuwa (lustro backendowego `_can_manage_template`): autor szablonu,
 * dyrektor, CEO, developer — także szablony firmowe (gwiazdka oznacza „globalny",
 * a nie „zamknięty dla autora"). Flagę „firmowy" przy dodawaniu i siatkę
 * domyślnych nadal ustawia tylko dyrektor/CEO.
 */

function errorMessage(err: unknown): string {
  const detail = (err as { response?: { data?: { errors?: { detail?: unknown } } } })?.response
    ?.data?.errors?.detail;
  if (typeof detail === "string") return detail;
  if (detail && typeof detail === "object") {
    const first = Object.values(detail as Record<string, unknown>).flat()[0];
    if (typeof first === "string") return first;
  }
  return "Nie udało się zapisać szablonu.";
}

export function FormatkaTemplatesSection() {
  const user = useAuthStore((s) => s.user);
  const { data, isLoading } = useFormatkaTemplates();
  const m = useFormatkaTemplateMutations();
  const [query, setQuery] = useState("");
  const [onlyMine, setOnlyMine] = useState(false);
  const [editing, setEditing] = useState<FormatkaTemplate | null>(null);
  const [creating, setCreating] = useState(false);
  const [historyOf, setHistoryOf] = useState<FormatkaTemplate | null>(null);

  const canCompany = !!user && (user.role === "director" || user.is_ceo);
  const canManage = (t: FormatkaTemplate) =>
    !!user &&
    (t.author?.id === user.id || user.role === "director" || user.is_ceo || user.is_developer);

  const rows = useMemo(() => {
    let list = data ?? [];
    if (onlyMine && user) list = list.filter((t) => t.author?.id === user.id);
    const q = query.trim().toLowerCase();
    if (q) list = list.filter((t) => t.name.toLowerCase().includes(q));
    return list;
  }, [data, onlyMine, query, user]);

  // First company template = a ready-made example of the block+attributes layout.
  const example = (data ?? []).find((t) => t.is_company && t.file?.url) ?? null;

  return (
    <div className="space-y-4">
      <p className="text-[13px] text-[var(--color-text-secondary)] leading-relaxed">
        Szablony <b>DXF</b> arkuszy „plan sytuacyjny" (formatki): papier + tabelka + legenda +
        okno mapy (viewport). Wgrany plik jest skanowany przy zapisie; podmiana pliku w
        edycji dokłada nową wersję, a poprzednie rewizje zostają do pobrania w historii.
      </p>

      <div className="space-y-3">
        <DefaultsPanel templates={data ?? []} canEdit={canCompany} />
        <HowToPanel example={example} />
        <VariablesPanel />
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <input
          className="input-base max-w-xs text-sm py-1.5"
          placeholder="Filtruj po nazwie…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <label className="flex items-center gap-1.5 text-xs font-mono cursor-pointer select-none">
          <input type="checkbox" checked={onlyMine} onChange={(e) => setOnlyMine(e.target.checked)} />
          Tylko moje
        </label>
        <div className="ml-auto">
          <Button size="sm" onClick={() => setCreating(true)}>+ Nowy szablon DXF</Button>
        </div>
      </div>

      {isLoading ? (
        <div className="py-10 flex justify-center"><Spinner /></div>
      ) : (
        <TableShell>
          <thead>
            <tr>
              <Th>Nazwa</Th>
              <Th>Operator</Th>
              <Th>Papier</Th>
              <Th>Zmienne</Th>
              <Th>Autor</Th>
              <Th>Wersja</Th>
              <Th className="text-right">Akcje</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((t) => (
              <tr key={t.id} className="border-t border-[var(--color-border-muted)] hover:bg-[var(--color-bg-elevated)] transition-colors">
                <td className="px-3 py-2.5">
                  <div className="font-medium text-[var(--color-text-primary)]">
                    {t.is_company && <span className="text-amber-500 mr-1" title="Szablon firmowy (globalny)">★</span>}
                    {t.name}
                  </div>
                  {t.description && (
                    <div className="text-[11px] text-[var(--color-text-muted)]">{t.description}</div>
                  )}
                </td>
                <td className="px-3 py-2.5 whitespace-nowrap font-mono text-[11px]">
                  {operatorShort(t.operator)}
                </td>
                <td className="px-3 py-2.5 whitespace-nowrap font-mono text-[11px]">
                  {FORMATKA_PAPER_LABEL[t.paper]}
                </td>
                <td className="px-3 py-2.5">
                  <div className="flex flex-wrap gap-1 max-w-md">
                    {t.variables.slice(0, 12).map((v) => (
                      <span key={v} className="text-[11px] font-mono border border-[var(--color-border)] px-1.5 py-0.5 bg-[var(--color-bg-base)]">
                        {v}
                      </span>
                    ))}
                    {t.variables.length > 12 && (
                      <span className="text-[11px] font-mono text-[var(--color-text-muted)]">+{t.variables.length - 12}</span>
                    )}
                    {t.variables.length === 0 && <span className="section-label">brak</span>}
                  </div>
                </td>
                <td className="px-3 py-2.5 whitespace-nowrap">{t.author?.name ?? "—"}</td>
                <td className="px-3 py-2.5 whitespace-nowrap">
                  {/* Klik = historia wgrań: kto i kiedy wgrał którą wersję pliku. */}
                  <button
                    className="font-mono text-[11px] border border-[var(--color-border)] px-1.5 py-0.5 bg-[var(--color-bg-base)] hover:text-[var(--color-primary)] hover:border-[var(--color-primary)] transition-colors"
                    title="Pokaż historię wersji"
                    onClick={() => setHistoryOf(t)}
                  >
                    v{t.current_version}
                  </button>
                </td>
                <td className="px-3 py-2.5">
                  <div className="flex justify-end items-center gap-1.5">
                    {t.file?.url && (
                      <ManagedFileDownload
                        file={t.file}
                        className="inline-flex items-center gap-1 text-[11px] font-mono text-[var(--color-text-muted)] hover:text-[var(--color-primary)]"
                        title={`Pobierz ${t.file.original_filename}`}
                      >
                        <Download size={13} /> Pobierz
                      </ManagedFileDownload>
                    )}
                    {canManage(t) && (
                      <>
                        <Button size="sm" variant="ghost" onClick={() => setEditing(t)}>Edytuj</Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => window.confirm(`Usunąć szablon „${t.name}"?`) && m.remove.mutate(t.id)}
                        >
                          Usuń
                        </Button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={7} className="px-3 py-10 text-center section-label tracking-wide">
                  {(data ?? []).length === 0
                    ? "Brak szablonów. Dodaj pierwszy szablon DXF (patrz instrukcja powyżej)."
                    : "Brak szablonów spełniających kryteria filtrowania."}
                </td>
              </tr>
            )}
          </tbody>
        </TableShell>
      )}

      {(creating || editing) && (
        <FormatkaTemplateFormModal
          template={editing}
          canCompany={canCompany}
          onClose={() => { setCreating(false); setEditing(null); }}
        />
      )}
      {historyOf && (
        <TemplateHistoryModal template={historyOf} onClose={() => setHistoryOf(null)} />
      )}
    </div>
  );
}

function TemplateHistoryModal({ template, onClose }: { template: FormatkaTemplate; onClose: () => void }) {
  const { data, isLoading } = useFormatkaTemplateVersions(template.id);
  const rows = data ?? [];

  return (
    <Modal open size="lg" onClose={onClose} title={`Historia wersji — ${template.name}`}>
      {isLoading ? (
        <div className="py-8 flex justify-center"><Spinner /></div>
      ) : (
        <div className="max-h-96 overflow-y-auto border border-[var(--color-border-muted)]">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-[var(--color-bg-elevated)]">
              <tr className="text-left">
                <th className="px-2 py-1.5 font-mono uppercase tracking-wide text-[10px] text-[var(--color-text-muted)]">Wersja</th>
                <th className="px-2 py-1.5 font-mono uppercase tracking-wide text-[10px] text-[var(--color-text-muted)]">Papier</th>
                <th className="px-2 py-1.5 font-mono uppercase tracking-wide text-[10px] text-[var(--color-text-muted)]">Wgrał(a)</th>
                <th className="px-2 py-1.5 font-mono uppercase tracking-wide text-[10px] text-[var(--color-text-muted)]">Kiedy</th>
                <th className="px-2 py-1.5 font-mono uppercase tracking-wide text-[10px] text-[var(--color-text-muted)] text-right">Plik</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((v) => (
                <tr key={v.id} className="border-t border-[var(--color-border-muted)] align-top">
                  <td className="px-2 py-1.5 whitespace-nowrap font-mono">
                    v{v.version}
                    {v.version === template.current_version && (
                      <span className="ml-1.5 text-[10px] uppercase tracking-wide px-1 py-0.5 bg-[var(--color-bg-elevated)] border border-[var(--color-border)] text-[var(--color-text-secondary)]">
                        obecna
                      </span>
                    )}
                  </td>
                  <td className="px-2 py-1.5 whitespace-nowrap font-mono text-[11px]">
                    {v.paper ? FORMATKA_PAPER_LABEL[v.paper] : "—"}
                  </td>
                  <td className="px-2 py-1.5">{v.uploaded_by_name ?? "—"}</td>
                  <td className="px-2 py-1.5 whitespace-nowrap font-mono text-[11px]">{fmtDateTime(v.created_at)}</td>
                  <td className="px-2 py-1.5 text-right">
                    {v.file?.url ? (
                      <ManagedFileDownload
                        file={v.file}
                        className="inline-flex items-center gap-1 text-[11px] font-mono text-[var(--color-text-muted)] hover:text-[var(--color-primary)]"
                        title={`Pobierz ${v.file.original_filename}`}
                      >
                        <Download size={13} /> {v.file.original_filename}
                      </ManagedFileDownload>
                    ) : (
                      <span className="section-label">brak pliku</span>
                    )}
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-2 py-6 text-center section-label tracking-wide">
                    Brak historii wgrań.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      <div className="flex justify-end pt-3">
        <Button variant="ghost" size="sm" onClick={onClose}>Zamknij</Button>
      </div>
    </Modal>
  );
}

/**
 * Siatka domyślnych szablonów — ENERGA/TAURON × działki WPW/urzędowe.
 *
 * Bez niej generator brał „pierwszy szablon z listy", więc właściwy trzeba było
 * ustawiać ręcznie przy każdej działce. Pusta komórka jest dozwolona i oznacza
 * dokładnie stare zachowanie (szablon firmowy) — dlatego nic tu nie jest wymagane.
 */
function DefaultsPanel({
  templates,
  canEdit,
}: {
  templates: FormatkaTemplate[];
  canEdit: boolean;
}) {
  const [open, setOpen] = useState(false);
  const { data: cells, isLoading } = useFormatkaDefaults(open);
  const save = useSaveFormatkaDefaults();

  const cell = (operator: string, scope: FormatkaScope) =>
    (cells ?? []).find((c) => c.operator === operator && c.scope === scope) ?? null;

  const unset = (cells ?? []).filter((c) => !c.template).length;

  return (
    <div className="card overflow-hidden">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-[var(--color-bg-elevated)] transition-colors"
      >
        <span className="font-mono text-xs font-bold uppercase tracking-wider text-[var(--color-text-primary)]">
          Domyślne szablony (operator × przeznaczenie)
        </span>
        <ChevronDown size={16} className={`text-[var(--color-text-muted)] transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="border-t border-[var(--color-border-muted)] px-4 py-3 space-y-3">
          <p className="text-[13px] text-[var(--color-text-secondary)] leading-relaxed">
            Szablon wskazany w komórce jest brany automatycznie przy tworzeniu zestawu
            formatek — bez wchodzenia w każdą działkę. O kolumnie decyduje operator (OSD)
            klienta projektu, o wierszu — czy zestaw powstaje dla działek prywatnych (WPW),
            czy urzędowych. Projektant nadal może zmienić szablon na konkretnym zestawie.
            Pusta komórka = jak dotychczas, czyli szablon firmowy.
          </p>
          {isLoading ? (
            <div className="py-6 flex justify-center"><Spinner /></div>
          ) : (
            <TableShell>
              <thead>
                <tr>
                  <Th>Przeznaczenie</Th>
                  <Th>ENERGA</Th>
                  <Th>TAURON</Th>
                </tr>
              </thead>
              <tbody>
                {(["wpw", "office"] as FormatkaScope[]).map((scope) => (
                  <tr key={scope} className="border-t border-[var(--color-border-muted)]">
                    <td className="px-3 py-2.5 whitespace-nowrap text-[13px] font-medium text-[var(--color-text-primary)]">
                      {FORMATKA_SCOPE_LABEL[scope]}
                    </td>
                    {["energa", "tauron"].map((operator) => {
                      const current = cell(operator, scope);
                      return (
                        <td key={operator} className="px-3 py-2.5">
                          <select
                            className="input-base text-sm py-1.5 w-full max-w-xs"
                            disabled={!canEdit || save.isPending}
                            value={current?.template ?? ""}
                            onChange={(e) =>
                              save.mutate([
                                { operator, scope, template: e.target.value || null },
                              ])
                            }
                          >
                            <option value="">— nie ustawiono (szablon firmowy) —</option>
                            {templates.map((t) => (
                              <option key={t.id} value={t.id}>
                                {t.name}
                                {t.operator !== SHARED_OPERATOR ? ` · ${operatorShort(t.operator)}` : ""}
                              </option>
                            ))}
                          </select>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </TableShell>
          )}
          {!canEdit && (
            <p className="text-[11px] text-[var(--color-text-muted)]">
              Podgląd — domyślne szablony ustawia dyrektor lub CEO.
            </p>
          )}
          {canEdit && unset > 0 && !isLoading && (
            <p className="text-[11px] font-mono text-amber-600">
              Nieustawionych komórek: {unset} — dla nich system nadal bierze pierwszy
              szablon firmowy.
            </p>
          )}
          {save.isError && (
            <p className="text-xs text-[var(--color-primary)] font-mono">{errorMessage(save.error)}</p>
          )}
        </div>
      )}
    </div>
  );
}

function HowToPanel({ example }: { example: FormatkaTemplate | null }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="card overflow-hidden">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-[var(--color-bg-elevated)] transition-colors"
      >
        <span className="font-mono text-xs font-bold uppercase tracking-wider text-[var(--color-text-primary)]">
          Jak przygotować szablon (oznaczanie pól)
        </span>
        <ChevronDown size={16} className={`text-[var(--color-text-muted)] transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="border-t border-[var(--color-border-muted)] px-4 py-3 space-y-3 text-[13px] text-[var(--color-text-secondary)] leading-relaxed">
          <p>
            Szablon to plik <b>DXF</b> z JEDNYM layoutem (paper space) zawierającym całą
            oprawę arkusza w milimetrach: ramkę, tabelkę, legendę, żółtą ramkę opisową,
            pasek podpisu — oraz <b>dokładnie jeden placeholder VIEWPORT</b> (okno mapy;
            NIE wkładaj go w blok).
          </p>
          <div className="bg-[var(--color-bg-elevated)] border border-[var(--color-border-muted)] px-3 py-2 text-[12px]">
            <p className="font-semibold text-[var(--color-text-primary)]">1 blok czy wiele?</p>
            <p className="mt-0.5">
              Dowolnie. Generator wypełnia atrybuty <b>wszystkich bloków</b> w layoucie i
              <b> każdy</b> tekst <span className="font-mono">#nazwa#</span>. Możesz zrobić:
              (a) całą tabelkę jako <b>jeden blok</b> z wieloma atrybutami, (b) <b>wiele
              bloków</b> — osobno tabelka, legenda, podpis (tak jest w przykładzie firmowym),
              albo (c) mieszać bloki z luźnym tekstem <span className="font-mono">#…#</span>.
              Liczba i nazwy bloków nie mają znaczenia.
            </p>
          </div>
          <div>
            <p className="font-semibold text-[var(--color-text-primary)]">Pola wypełniane automatycznie oznacz na dwa sposoby:</p>
            <ol className="list-decimal ml-5 mt-1 space-y-1">
              <li>
                <b>Blok z atrybutami (zalecane)</b> — zrób z tabelki BLOK, a w komórkach
                wstaw <b>atrybuty</b> (komenda <span className="font-mono">ATTDEF</span>),
                gdzie <b>Tag = nazwa zmiennej</b> (np. <span className="font-mono">PROJEKT_NR_UMOWY</span>,{" "}
                <span className="font-mono">RYS_NR</span>, <span className="font-mono">ARKUSZY_LACZNIE</span>).
                Generator podmienia tekst atrybutu na wartość.
              </li>
              <li>
                <b>Tekst z <span className="font-mono">#nazwa#</span></b> — w zwykłym TEXT/MTEXT
                wpisz np. <span className="font-mono">#skala#</span>. Zadziała tak samo.
              </li>
            </ol>
            <p className="mt-1 text-[12px] text-[var(--color-text-muted)]">
              Wielkość liter i polskie znaki nie mają znaczenia. Pełna lista tagów →
              panel „Dostępne zmienne" poniżej.
            </p>
          </div>
          <div className="bg-[var(--color-bg-elevated)] border border-[var(--color-border-muted)] px-3 py-2 font-mono text-[12px]">
            <p className="text-[var(--color-text-muted)] mb-1">// Przykład — tabelka jako blok z atrybutami (tagi):</p>
            <div className="grid grid-cols-2 gap-x-6 gap-y-0.5">
              <span>NR UMOWY → PROJEKT_NR_UMOWY</span>
              <span>OBI/WP → PROJEKT_NR_ST · PROJEKT_NR_WP</span>
              <span>TEMAT → TEMAT</span>
              <span>INWESTOR → KLIENT_NAZWA · KLIENT_ODDZIAL_NAZWA</span>
              <span>PROJEKTOWAŁ → PROJEKT_PROJEKTANT</span>
              <span>nr upr. → PROJEKTANT_NR_UPRAWNIEN</span>
              <span>OPRACOWAŁ (ja) → UZYTKOWNIK_IMIE_NAZWISKO</span>
              <span>BRANŻA → BRANZA</span>
              <span>DATA → DATA</span>
              <span>SKALA → SKALA</span>
              <span>NR RYS. → RYS_NR</span>
              <span>LICZBA RYS. → ARKUSZY_LACZNIE</span>
            </div>
          </div>
          {example?.file?.url && (
            <p>
              Gotowy przykład (blok z atrybutami) —{" "}
              <ManagedFileDownload
                file={example.file}
                className="inline-flex items-center gap-1 font-mono text-[var(--color-primary)] hover:underline"
              >
                <Download size={13} /> pobierz „{example.name}"
              </ManagedFileDownload>{" "}
              i otwórz w AutoCAD, żeby zobaczyć tagi w tabelce.
            </p>
          )}
          <div className="bg-[var(--color-bg-elevated)] border border-[var(--color-border-muted)] px-3 py-2 text-[12px]">
            <p className="font-semibold text-[var(--color-text-primary)]">Kod QR na arkuszach (opcjonalnie)</p>
            <p className="mt-0.5">
              Narysuj w layoucie <b>jeden kwadrat</b> na warstwie{" "}
              <span className="font-mono">FORMATKA_QR</span> — bok min. <b>12 mm</b>{" "}
              (zalecane 18 mm). Generator podmieni go na każdym arkuszu na wektorowy
              kod QR z danymi arkusza: zestaw, numer arkusza, wersja trasy, działka
              (TERYT) i numery zleceń WPW. Liczy się <b>obrys warstwy</b>, więc kwadrat
              może być polilinią albo 4 liniami; napisy TEXT/MTEXT na tej warstwie są
              ignorowane, ale nie kładź tam żadnej innej geometrii. Brak warstwy =
              brak QR (nic się nie psuje).
            </p>
          </div>
          <p className="text-[12px] text-[var(--color-text-muted)]">
            Warstwy szablonu nazywaj z prefiksem <span className="font-mono">FORMATKA_</span>
            (upload ostrzeże o innych, ale ich nie zablokuje).
          </p>
        </div>
      )}
    </div>
  );
}

function VariablesPanel() {
  const [open, setOpen] = useState(false);
  const { data: groups, isLoading } = useFormatkaVariables(open);
  return (
    <div className="card overflow-hidden">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-[var(--color-bg-elevated)] transition-colors"
      >
        <span className="font-mono text-xs font-bold uppercase tracking-wider text-[var(--color-text-primary)]">
          Dostępne zmienne (tagi ATTDEF / #nazwa#)
        </span>
        <ChevronDown size={16} className={`text-[var(--color-text-muted)] transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open &&
        (isLoading ? (
          <div className="py-8 flex justify-center"><Spinner /></div>
        ) : (
          (groups ?? []).map((g) => (
            <div key={g.key} className="border-t border-[var(--color-border-muted)] px-4 py-3">
              <h3 className="font-mono text-[11px] font-bold uppercase tracking-wider text-[var(--color-text-secondary)]">{g.label}</h3>
              <p className="mt-0.5 text-[11px] text-[var(--color-text-muted)]">{g.description}</p>
              <table className="mt-2 w-full text-xs">
                <tbody>
                  {g.variables.map((v) => (
                    <tr key={v.name} className="border-t border-[var(--color-border-muted)]">
                      <td className="py-1.5 pr-3 whitespace-nowrap align-top w-px">
                        <span className="text-[11px] font-mono border border-[var(--color-border)] px-1.5 py-0.5 bg-[var(--color-bg-base)]">{v.name}</span>
                      </td>
                      <td className="py-1.5 text-[var(--color-text-secondary)]">{v.description}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {g.note && <p className="mt-2 text-[11px] text-[var(--color-text-muted)] italic">{g.note}</p>}
            </div>
          ))
        ))}
    </div>
  );
}

// Ręczny override orientacji — tylko standardowe klasy mają kanoniczne wymiary
// (wstęga/custom zawsze ze skanu pliku).
const MANUAL_PAPERS: FormatkaPaper[] = [
  "a4_portrait", "a4_landscape", "a3_portrait", "a3_landscape",
];

function FormatkaTemplateFormModal({
  template,
  canCompany,
  onClose,
}: {
  template: FormatkaTemplate | null;
  canCompany: boolean;
  onClose: () => void;
}) {
  const m = useFormatkaTemplateMutations();
  const [name, setName] = useState(template?.name ?? "");
  const [description, setDescription] = useState(template?.description ?? "");
  const [isCompany, setIsCompany] = useState(template?.is_company ?? false);
  const [operator, setOperator] = useState(template?.operator ?? SHARED_OPERATOR);
  const [paper, setPaper] = useState<FormatkaPaper | null>(template?.paper ?? null);
  const [file, setFile] = useState<File | null>(null);
  const [warnings, setWarnings] = useState<string[] | null>(null);
  const mutation = template ? m.update : m.create;

  // Nietknięty select = nie wysyłamy `paper` (świeży upload klasyfikuje skan);
  // zmieniony = jawny wybór użytkownika, który na backendzie wygrywa ze skanem.
  const paperOverride =
    template && paper && paper !== template.paper && MANUAL_PAPERS.includes(paper)
      ? paper
      : undefined;

  const submit = () => {
    if (!name.trim() || (!template && !file)) return;
    if (template) {
      m.update.mutate(
        {
          id: template.id, name: name.trim(), description: description.trim(), operator,
          paper: paperOverride, file: file ?? undefined,
        },
        { onSuccess: (t) => { setWarnings(t.layer_warnings ?? null); if (!t.layer_warnings?.length) onClose(); } },
      );
    } else {
      m.create.mutate(
        { name: name.trim(), description: description.trim(), is_company: isCompany, operator, file: file! },
        { onSuccess: (t) => { setWarnings(t.layer_warnings ?? null); if (!t.layer_warnings?.length) onClose(); } },
      );
    }
  };

  return (
    <Modal open onClose={onClose} title={template ? "Edytuj szablon formatki" : "Nowy szablon formatki"}>
      <div className="space-y-3">
        <label className="block">
          <span className="section-label">Nazwa szablonu *</span>
          <input className="input-base mt-1" value={name} autoFocus onChange={(e) => setName(e.target.value)} />
        </label>

        <label className="block">
          <span className="section-label">Opis</span>
          <textarea className="input-base mt-1 min-h-[60px]" value={description} onChange={(e) => setDescription(e.target.value)} />
        </label>

        <label className="block">
          <span className="section-label">Operator (OSD)</span>
          <select className="input-base mt-1" value={operator} onChange={(e) => setOperator(e.target.value)}>
            <option value="inny">Uniwersalny (oba)</option>
            <option value="energa">ENERGA-OPERATOR</option>
            <option value="tauron">TAURON Dystrybucja</option>
          </select>
          <p className="mt-1 text-[11px] text-[var(--color-text-muted)]">
            Tylko etykieta — porządkuje listy wyboru. To, który szablon jest{" "}
            <b>domyślny</b>, ustawia się w panelu „Domyślne szablony" na liście.
          </p>
        </label>

        {template && paper && (
          <label className="block">
            <span className="section-label">Papier / orientacja</span>
            <select
              className="input-base mt-1"
              value={paper}
              onChange={(e) => setPaper(e.target.value as FormatkaPaper)}
            >
              {!MANUAL_PAPERS.includes(template.paper) && (
                <option value={template.paper}>
                  {FORMATKA_PAPER_LABEL[template.paper]} (ze skanu pliku)
                </option>
              )}
              {MANUAL_PAPERS.map((p) => (
                <option key={p} value={p}>{FORMATKA_PAPER_LABEL[p]}</option>
              ))}
            </select>
            <p className="mt-1 text-[11px] text-[var(--color-text-muted)]">
              Zmiana ręcznie nadpisuje wynik skanu DXF (także przy jednoczesnej
              podmianie pliku) — użyj, gdy szablon został błędnie odczytany jako
              pionowy/poziomy. Nietknięte pole = orientacja ze skanu.
            </p>
          </label>
        )}

        {!template && canCompany && (
          <label className="flex items-start gap-2 text-[13px] cursor-pointer select-none">
            <input type="checkbox" className="accent-[var(--color-primary)] mt-0.5" checked={isCompany} onChange={(e) => setIsCompany(e.target.checked)} />
            <span>
              <b>Szablon firmowy (globalny)</b> — oznaczony gwiazdką, proponowany jako
              domyślny. Edytować i usuwać może jego autor, dyrektor, CEO lub developer.
              Odznacz, aby dodać jako <b>własny</b>.
            </span>
          </label>
        )}

        <div>
          <span className="section-label">
            {template
              ? `Wgraj nową wersję pliku DXF (opcjonalnie — obecnie v${template.current_version})`
              : "Plik szablonu DXF (.dxf) *"}
          </span>
          <FileDropZone
            className="mt-1"
            accept=".dxf"
            files={file ? [file] : []}
            onFilesChange={(fs) => { setFile(fs[0] ?? null); setWarnings(null); }}
          />
          <p className="mt-1 text-[11px] text-[var(--color-text-muted)]">
            1 niepusty layout + 1 placeholder VIEWPORT (poza blokiem). Pola: atrybuty
            (ATTDEF) w dowolnej liczbie bloków lub <span className="font-mono">#nazwa#</span>{" "}
            w tekście. Opcjonalnie: kwadrat ≥12 mm na warstwie{" "}
            <span className="font-mono">FORMATKA_QR</span> = kod QR na każdym arkuszu —
            patrz „Jak przygotować szablon" na liście.
            {template && " Poprzednia wersja zostaje w historii i nadal da się ją pobrać."}
          </p>
        </div>

        {warnings && warnings.length > 0 && (
          <div className="text-[11px] font-mono text-amber-600 bg-amber-50/60 border border-amber-200 px-2 py-1.5">
            Zapisano. Warstwy bez prefiksu FORMATKA_: {warnings.join(", ")}. Możesz zamknąć.
          </div>
        )}
        {mutation.isError && (
          <p className="text-xs text-[var(--color-primary)] font-mono">{errorMessage(mutation.error)}</p>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <Button variant="ghost" size="sm" onClick={onClose}>{warnings ? "Zamknij" : "Anuluj"}</Button>
          <Button size="sm" disabled={!name.trim() || (!template && !file)} loading={mutation.isPending} onClick={submit}>
            {template ? "Zapisz" : "Utwórz"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
